/**
 * Which versions the timeline shows.
 *
 * The table holds three kinds of row and the panel used to render all three the
 * same way, which is why restoring appeared to make the history *longer*:
 *
 *   checkpoint  the author pressed Save Version and named it
 *   auto-save   the edit event fired and the five-minute throttle allowed it
 *   rescue      a restore was about to overwrite work that was not stored yet,
 *               so it stored it first — "Unsaved work before v3"
 *
 * Only the first is history in the sense anyone means it. The other two are the
 * system protecting the author from itself, and they belong behind a switch.
 * See lilia-docs 2026-09-08-document-history-api for how each one is written.
 *
 * The API already distinguishes them — `isAutoSave` is true for the last two —
 * so this is a reading of data that was always there, not a new contract.
 */

export interface TimelineVersion {
  id: string;
  isAutoSave?: boolean;
  isCurrent?: boolean;
}

/**
 * The rows to render.
 *
 * The current version is always kept, even when it is automatic. It is where
 * the document *is*; hiding it would leave the timeline with nothing marked and
 * no way to explain why — which is a worse confusion than the one being fixed.
 */
export function visibleVersions<T extends TimelineVersion>(
  versions: T[],
  showAutomatic: boolean,
): T[] {
  if (showAutomatic) return versions;
  return versions.filter((v) => !v.isAutoSave || v.isCurrent);
}

/** How many rows the switch would reveal — 0 when it would reveal nothing. */
export function hiddenCount(versions: TimelineVersion[], showAutomatic: boolean): number {
  return versions.length - visibleVersions(versions, showAutomatic).length;
}

/**
 * Whether the filter is the reason the list looks empty.
 *
 * A document with only automatic saves is a real state — nobody has pressed
 * Save Version yet — and "no versions saved yet" would be a lie when there are
 * several sitting behind the switch.
 */
export function emptyBecauseFiltered(
  versions: TimelineVersion[],
  showAutomatic: boolean,
): boolean {
  return versions.length > 0 && visibleVersions(versions, showAutomatic).length === 0;
}
